import { HttpRequest } from "./httpClient";
import { tracer } from '../instrumentation';

import { createLogger } from '../utils/logger';
const logger = createLogger('utils');

// URL base de la API
const API_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud';
const request = new HttpRequest(API_URL);

// ──────────────────────────────────────────────
// Interfaces
// ──────────────────────────────────────────────

/**
 * Estructura general de una cuenta (usada para vistas u otros props)
 */
export interface Account {
  id: string;
  name: string;
}

/**
 * Estructura detallada de una cuenta (API)
 */
export interface AccountData {
  id: string;
  companyName: string;
  companySizeId: string;
  createdAt: number;
  updatedAt: number;
  adminId: string;
  companySize: string;
  status: string;
}

// ──────────────────────────────────────────────
// Constantes
// ──────────────────────────────────────────────

/**
 * Mapeo de tamaños de empresa
 */
export const companySizes: Record<string, string> = {
  "1": "1-5",
  "2": "6-10",
  "3": "11-49",
  "4": "50-99",
  "5": "100-999",
  "6": "1000+"
};

// ──────────────────────────────────────────────
// Funciones API
// ──────────────────────────────────────────────

/**
 * Busca todas las cuentas disponibles
 * @returns Lista de cuentas encontradas
 */
export const findAccounts = async (): Promise<AccountData[] | undefined> => {
  logger.info("Buscando cuentas...");
  const span = tracer.startSpan('findAccounts');
  try {
    const response = await request.get('accounts');
    logger.info("API Response:", response);
    logger.info("Response data structure:", JSON.stringify(response.data, null, 2));
    if (response.status === 200) {
      if (response.data.accounts) {
        return response.data.accounts;
      } else if (response.data.data) {
        return response.data.data;
      } else {
        logger.info("Unexpected data structure:", response.data);
        return undefined;
      }
    }
    logger.info("Unexpected response status:", response.status);
    return undefined;
  } catch (error) {
    logger.error("Error al buscar cuentas:", error);
    span.recordException(error);
    return undefined;
  } finally {
    span.end();
  }
};

/**
 * Busca una cuenta específica por su ID
 * @param accountId ID de la cuenta a buscar
 * @returns Datos de la cuenta encontrada o null
 */
export const findAccountById = async (accountId: string): Promise<AccountData | null> => {
  const span = tracer.startSpan('findAccountById');
  try {
    const response = await request.get(`accounts/${accountId}`);
    if (response.status === 200 && response.data) {
      return response.data?.accountData;
    }
    return null;
  } catch (error) {
    logger.error("Error al buscar cuenta por ID:", error);
    span.recordException(error);
    return null;
  } finally {
    span.end();
  }
};

/**
 * Crea una nueva cuenta
 * @param newAccount Datos de la nueva cuenta
 * @returns Respuesta del backend
 */
export const createAccount = async (newAccount: any): Promise<any> => {
  const span = tracer.startSpan('createAccount');
  try {
    const response = await request.post(newAccount, 'accounts');
    return response;
  } catch (error) {
    logger.error("Error al crear cuenta:", error);
    span.recordException(error);
    throw error;
  } finally {
    span.end();
  }
};

/**
 * Actualiza el estado de una cuenta (por ejemplo, 'active' o 'innactive')
 * @param accountId ID de la cuenta a actualizar
 * @param newStatus Nuevo estado ('active' | 'innactive')
 * @returns Respuesta del backend
 */
export const updateAccountStatus = async (
  accountId: string,
  newStatus: 'active' | 'innactive'
): Promise<any> => {
  const span = tracer.startSpan('updateAccountStatus');
  try {
    const response = await request.put(`accounts/${accountId}`, { status: newStatus });
    return response;
  } catch (error) {
    logger.error(`Error al actualizar el estado de la cuenta ${accountId}:`, error);
    span.recordException(error);
    throw error;
  } finally {
    span.end();
  }
};

